import LayoutContainer from 'components/layout/LayoutContainer';
import React, { useCallback, useEffect } from 'react';
import Card from 'components/inputs/Card';
import { FETCH_PRELOADED_CUSTOMER_TRANSACTION } from 'actions/customer';
import { useDispatch, useSelector } from 'react-redux';
import * as _ from 'lodash';
import { useParams } from 'react-router-dom';
import ReactPagination from 'components/general/ReactPagination';
import moment from 'moment';
import { Col, Row } from 'react-bootstrap';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

/**
 * CDB-1085 — Pre-loaded credit movements for a customer.
 *
 * Kept in its own tab rather than merged into the wallet Transactions list: the
 * pre-loaded balance is a counter separate from the wallet, so showing both in
 * one table would put two unrelated running balances in the same column. Each
 * ledger movement (added / used / expired / refunded) is one row, tagged with
 * the campaign it belongs to.
 */
const PreloadedTransaction = () => {
  const dispatch = useDispatch();
  const { customerId } = useParams();

  const preloadedTransactionList = useSelector((state) => state.customerTransaction.preloadedTransaction);
  const isLoading = useSelector((state) => state.customerTransaction.preloadedIsLoading);
  const page = useSelector((state) => state.customerTransaction.preloadedPage);
  const limit = useSelector((state) => state.customerTransaction.preloadedLimit);
  const totalPages = useSelector((state) => state.customerTransaction.preloadedTotalPages);
  const totalData = useSelector((state) => state.customerTransaction.preloadedTotal);

  const getPreloadedTransaction = useCallback(
    (data = {}) => {
      dispatch({ type: FETCH_PRELOADED_CUSTOMER_TRANSACTION.REQUEST, payload: { ...data, id: customerId } });
    },
    [dispatch, customerId]
  );

  useEffect(() => {
    getPreloadedTransaction();
  }, [getPreloadedTransaction]);

  const handlePageClick = (pageVal) => {
    getPreloadedTransaction({ page: pageVal.selected + 1 });
  };

  return (
    <React.Fragment>
      <LayoutContainer>
        <SkeletonTheme baseColor="#1b2a38" highlightColor="#15212b" enableAnimation="true">
          <Card className="customerDetail-table">
            <Row className="customerBooking-table-heading-row">
              <Col xl={11} lg={11} md={11}>
                <div className="customerDetail-table-heading">
                  {isLoading ? <Skeleton height={15} width={100} /> : 'Pre-loaded Transactions'}
                </div>
              </Col>
            </Row>
            <div className="customerDetail-list-table">
              <div className="table-responsive">
                <table className="record-list-table">
                  <thead>
                    <tr>
                      <th>{isLoading ? <Skeleton height={15} width={100} /> : 'Sr No'}</th>
                      <th>{isLoading ? <Skeleton height={15} width={100} /> : 'Date & Time'}</th>
                      <th>{isLoading ? <Skeleton height={15} width={100} /> : 'Type'}</th>
                      <th>{isLoading ? <Skeleton height={15} width={100} /> : 'Message'}</th>
                      <th>{isLoading ? <Skeleton height={15} width={100} /> : 'Campaign'}</th>
                      <th>{isLoading ? <Skeleton height={15} width={100} /> : 'Booking ID'}</th>
                      <th>{isLoading ? <Skeleton height={15} width={100} /> : 'Previous Balance'}</th>
                      <th>{isLoading ? <Skeleton height={15} width={100} /> : 'Transaction Amount'}</th>
                      <th>{isLoading ? <Skeleton height={15} width={100} /> : 'Updated Balance'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {_.isEmpty(preloadedTransactionList) ? (
                      <tr>
                        <td colSpan={9} className="border-0">
                          <div className="empty-data-block">No pre-loaded transactions</div>
                        </td>
                      </tr>
                    ) : (
                      _.map(preloadedTransactionList, (item, index) => {
                        const previousBalance = _.get(item, 'current_balance', 0);
                        const amount = _.get(item, 'amount', 0);
                        const isCredit = _.get(item, 'type') === 'credit';
                        const updatedBalance = isCredit ? previousBalance + amount : previousBalance - amount;
                        return (
                          <tr key={`preloaded-transaction-item-${index}`}>
                            <td>{isLoading ? <Skeleton height={15} width={100} /> : (page - 1) * limit + index + 1}</td>
                            <td>
                              {isLoading ? (
                                <Skeleton height={15} width={100} />
                              ) : item.createdAt ? (
                                moment(item.createdAt).utc(true).format('DD/MM/YYYY  H:mm:ss')
                              ) : (
                                '-'
                              )}
                            </td>
                            <td>{isLoading ? <Skeleton height={15} width={100} /> : _.get(item, 'type', '-')}</td>
                            <td>{isLoading ? <Skeleton height={15} width={100} /> : _.get(item, 'message', '-')}</td>
                            <td>{isLoading ? <Skeleton height={15} width={100} /> : _.get(item, 'preloaded_campaign', '-')}</td>
                            <td>{isLoading ? <Skeleton height={15} width={100} /> : _.get(item, 'booking.id', '-')}</td>
                            <td>{isLoading ? <Skeleton height={15} width={100} /> : Number(previousBalance).toFixed(1)}</td>
                            <td>
                              {isLoading ? (
                                <Skeleton height={15} width={100} />
                              ) : (
                                `${isCredit ? '+' : '-'}${Number(amount).toFixed(1)}`
                              )}
                            </td>
                            <td>{isLoading ? <Skeleton height={15} width={100} /> : Number(updatedBalance).toFixed(1)}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </SkeletonTheme>
      </LayoutContainer>
      {!_.isEmpty(preloadedTransactionList) && (
        <ReactPagination
          currentPage={page}
          limit={limit}
          total={totalData}
          handlePageClick={(pageVal) => handlePageClick(pageVal)}
          totalPages={totalPages}
          marginPagesDisplayed={1}
        />
      )}
    </React.Fragment>
  );
};

export default PreloadedTransaction;
